import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { KeyboardType, ReturnKeyTypeOptions, TextInput, TouchableOpacity } from 'react-native';
import { MBView, MBViewProps } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBAnimationBounceIn } from '@shared/components/animation';
import { MBTypographyStylesRaw } from '@shared/components/typography';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { MBIconV2 } from '../image/iconV2';
import { Styles } from './styles';

export interface MBSearchBarProps extends MBViewProps {
  placeholder?: string;
  onChangeText(text: string): void;
  onSubmitEditing?(): void;

  value?: string;
  initialValue?: string;

  hideIcon?: boolean;

  autoFocus?: boolean;
  keyboardType?: KeyboardType;
  onBlur?(): void;
  onFocus?(): void;
  onClear?(): void;
  forwardRef?: MutableRefObject<TextInput | null>;
  returnKeyType?: ReturnKeyTypeOptions;
  testID?: string;
}

export function MBSearchBar({
  forwardRef,
  value: defaultValue = '',
  style,
  autoFocus = false,
  hideIcon,
  keyboardType,
  onChangeText,
  onBlur,
  onFocus,
  onClear,
  onSubmitEditing,
  placeholder = 'Search...',
  returnKeyType = 'search',
  testID,
  ...otherProps
}: MBSearchBarProps) {
  const colors = useContext(ColorThemeContext);
  const textInput = useRef<TextInput>(null);
  const [value, setValue] = useState(defaultValue);
  const [focused, setFocused] = useState(autoFocus);

  if (forwardRef && textInput.current) {
    forwardRef.current = textInput.current;
  }

  const handleBlur = useCallback(() => {
    setFocused(false);
    !!onBlur && onBlur();
  }, [setFocused, onBlur]);

  const handleFocus = useCallback(() => {
    setFocused(true);
    !!onFocus && onFocus();
  }, [setFocused, onFocus]);

  const handleChangeText = useCallback(
    (updatedValue: string) => {
      setValue(updatedValue);
      onChangeText(updatedValue);
    },
    [setValue, onChangeText],
  );

  useEffect(() => setValue(defaultValue), [defaultValue]);

  // Make the text input focused whenever we click the search bar. Even if we click
  // on the icon or other non-textinput area, the keyboard should appear.
  const handlePress = useCallback(() => textInput.current?.focus(), [textInput]);
  const handleClear = useCallback(() => {
    textInput.current?.focus();
    handleChangeText('');
    !!onClear && onClear();
  }, [textInput, handleChangeText, onClear]);

  return (
    <TouchableOpacity activeOpacity={1} onPress={handlePress}>
      <MBView
        {...otherProps}
        center
        row
        style={[
          Styles.container,
          {
            backgroundColor: focused ? Colors.v2.white : colors.input.backgroundInactive,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: focused ? Colors.v2.green30 : 'transparent',
          },
          style,
        ]}
      >
        {!hideIcon && (
          <View center paddingHorizontal={1.5}>
            <MBIconV2 name="magnifying-search-01" color={colors.input.label} size={grid(3)} />
          </View>
        )}
        {!!hideIcon && <View paddingLeft={2} />}
        <View flex>
          <TextInput
            ref={textInput}
            value={value}
            autoFocus={autoFocus}
            style={[
              MBTypographyStylesRaw.body1,
              Styles.textInput,
              { color: colors.input.foregroundActive },
            ]}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChangeText={handleChangeText}
            onSubmitEditing={onSubmitEditing}
            placeholder={placeholder}
            placeholderTextColor={colors.input.label}
            underlineColorAndroid="transparent"
            returnKeyType={returnKeyType}
            keyboardType={keyboardType}
            testID={testID}
          />
        </View>

        <MBAnimationBounceIn disabled={!value}>
          <TouchableOpacity onPress={handleClear}>
            <MBIconV2 name="remove-filled" color={Colors.v2.gray10} size={grid(5)} />
          </TouchableOpacity>
        </MBAnimationBounceIn>
      </MBView>
    </TouchableOpacity>
  );
}
